import dayjs, { Dayjs } from 'dayjs';

// Friperiod- och medlemsårslogik för utskicks-wizarden. Speglar backend (InvoiceService.GetNextExpireDate):
// en betalning förnyar till förbundets förfallodatum (expireDate MMdd) för året av (betaldatum + gracePeriod dagar).
// Betalar man inom friperioden (de sista gracePeriod dagarna före förfallodatumet) rullar medlemskapet till nästa år.

// Plockar ut MM och DD ur en MMdd-sträng (t.ex. "1231").
const parseMMdd = (expireMMdd: string): { mm: string; dd: string } | null => {
  if (!/^\d{4}$/.test(expireMMdd ?? '')) return null;
  return { mm: expireMMdd.slice(0, 2), dd: expireMMdd.slice(2, 4) };
};

// Nästa förfallodatum (år-MMdd) på eller efter ett givet datum.
const nextExpireOnOrAfter = (from: Dayjs, mm: string, dd: string): Dayjs => {
  let target = dayjs(`${from.year()}-${mm}-${dd}`);
  if (from.isAfter(target, 'day')) target = target.add(1, 'year');
  return target;
};

export interface MembershipYearResult {
  // Förfallodatumet (expireDate MMdd) som friperioden räknas bakåt från.
  target: Dayjs;
  // Friperiodens start = target − gracePeriod dagar. Betalning på/efter detta datum ger nästa års medlemskap.
  friStart: Dayjs;
  // Medlemskapets nya förfallodatum givet valt betaldatum.
  expiryDate: Dayjs;
  // Medlemsåret aviseringen gäller (= expiryDate.year()).
  expiryYear: number;
  // Bas-år för medlemsurvalet i steg 2 (medlemmarnas nuvarande förfallo-år): expiryYear − 1.
  selectionYear: number;
  // true om friperiodens start hamnar inuti betalningsfönstret (utskicksdatum, betaldatum] → olika medlemmar
  // skulle få olika medlemsår från samma avisering. Måste undvikas.
  straddles: boolean;
  // Senaste betaldatum som håller hela fönstret före friperioden (= friStart − 1 dag).
  latestSafePayDate: Dayjs;
  // true om förbundet har en friperiod alls (gracePeriod > 0). Är den 0 finns INGEN friperiod – då är friStart == target
  // och alla friperiod-specifika varningar/visualiseringar ska döljas.
  hasFriPeriod: boolean;
  // true när aviseringen gäller innevarande år (betaldatum före friperioden) men friperioden ändå startar därefter –
  // dvs. SENA betalningar (efter förfallodatumet) som kommer in under friperioden ger medlemmen nästa års medlemskap.
  latePaymentSpill: boolean;
}

// Härleder medlemsår + friperiod utifrån utskicksdatum, betaldatum, förbundets förfallodatum och friperiod.
// Returnerar null om indata saknas (t.ex. inget förfallodatum konfigurerat).
export const deriveMembershipYear = (opts: {
  sendDate: Dayjs | null;
  payDate: Dayjs | null;
  expireMMdd: string | null | undefined;
  graceDays: number;
}): MembershipYearResult | null => {
  const { sendDate, payDate, expireMMdd, graceDays } = opts;
  if (!sendDate || !expireMMdd) return null;
  const parsed = parseMMdd(expireMMdd);
  if (!parsed) return null;

  const grace = Number.isFinite(graceDays) && graceDays > 0 ? graceDays : 0;
  const hasFriPeriod = grace > 0;
  const target = nextExpireOnOrAfter(sendDate, parsed.mm, parsed.dd);
  const friStart = target.subtract(grace, 'day');
  const latestSafePayDate = friStart.subtract(1, 'day');

  // Betalar man på/efter friperiodens start rullar förfallodatumet ett år framåt.
  const paidInFriPeriod = !!payDate && !payDate.isBefore(friStart, 'day');
  const expiryDate = paidInFriPeriod ? target.add(1, 'year') : target;
  const expiryYear = expiryDate.year();

  // Straddle: friperiodens start ligger inuti (utskicksdatum, betaldatum]. Utan friperiod (grace 0) finns ingen
  // sådan gräns att korsa – då kan straddle aldrig inträffa.
  const straddles = hasFriPeriod && !!payDate && friStart.isAfter(sendDate, 'day') && !friStart.isAfter(payDate, 'day');
  // Sena betalningar spiller in i friperioden: aviseringen gäller innevarande år (betaldatum före friperioden),
  // men friperioden startar därefter → betalningar som kommer in sent (under friperioden) ger nästa års medlemskap.
  const latePaymentSpill = !!payDate && grace > 0 && payDate.isBefore(friStart, 'day');

  return {
    target,
    friStart,
    expiryDate,
    expiryYear,
    selectionYear: expiryYear - 1,
    straddles,
    latestSafePayDate,
    latePaymentSpill,
    hasFriPeriod,
  };
};
